/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Button, Card, CardActions, CardContent, Container, Grid, IconButton, InputAdornment, InputLabel, TextField, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { defaultLocale, extractLocaleFromPath, getTranslation, languages, PATH_FORGOT_PASSWORD, supportedLocales } from 'src/utils';
import { AlternateEmail, Visibility, VisibilityOff } from '@material-ui/icons';
import { Link, useLocation } from 'react-router-dom';
import { getErrorMessage } from 'src/utils/getAxiosErrorMessage';
import { LoginForm } from './LoginModels';
import { CircleIcon, Footer, UnauthorizedHeader } from '../../components';
import { useStyles } from './LoginStyles';
import { getUserLoginData, loginRepresentative, getTranslationsByBrokerId } from './LoginService';
import { useApiError, useAppData, useLoading } from '../../providers';
import { clearThemeCache } from '../../layouts';
import { setAmplitudeIdentity, trackAmplitudeData } from '../../services/amplitudeService';

export const LoginAsRepresentative: FC = () => {
  const { logoUrl, footerLogoUrl, pagePath, saveUserData, saveToken, removeToken, saveTranslations, authToken, translations, saveLocale } = useAppData();
  const history = useHistory();
  const { isLoading, setIsLoading } = useLoading();
  const classes = useStyles();
  const { addError } = useApiError();
  const { enqueueSnackbar } = useSnackbar();
  const [showError, setShowError] = useState<boolean>(false);
  const [logo, setLogo] = useState<string>('');
  const [footerLogo, setFooterLogo] = useState<string>('');
  const [translation, setTranslation] = useState<any>();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [selectedLocale, setSelectedLocale] = useState<any>(languages[0]);
  const location = useLocation();
  const lang = extractLocaleFromPath(location.pathname);
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginForm>();
  const { ref: emailHookRef, ...emailHookRefProps } = register('userName', {
    required: true, // translation.text_1358,
    validate: {
      validEmail: (value) => {
        const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i;
        return regex.test(value.replace(/\s/g, ''));
      },
    },
  });
  const { ref: passwordHookRef, ...passwordHookRefProps } = register('password', {
    required: true, // translation.text_1359,
  });

  useEffect(() => {
    setLogo(logoUrl);
    setFooterLogo(footerLogoUrl);
  }, [logoUrl, footerLogoUrl]);

  const getTranslationList = useCallback(
    async (newLocale: string) => {
      try {
        setIsLoading(true);
        const brokerID = process.env.REACT_APP_BROKER_ID;
        const translations = await getTranslationsByBrokerId(brokerID);
        saveTranslations(JSON.stringify(translations.data));
        setTranslation(translations.data);
        saveLocale(newLocale);
      } catch (error: unknown) {
        addError(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    },
    [saveTranslations, saveLocale, addError, setIsLoading],
  );

  const handleLocaleChange = useCallback(
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      const selectedLanguage = languages.find((item) => item.value === (event.target.value as string)) || languages[0];
      setSelectedLocale(selectedLanguage);
      if (selectedLanguage.value !== languages[0].value) {
        trackAmplitudeData('partnerPortal_action_languageChanged', {
          page_language: selectedLanguage.value,
        });
      }
      saveLocale(selectedLanguage.value);
      await getTranslationList(selectedLanguage.value);
    },
    [getTranslationList],
  );

  useEffect(() => {
    (async () => {
      if (lang && supportedLocales.includes(lang)) {
        const selectedLanguage = languages.find((item) => item.value === lang) || languages[0];
        setSelectedLocale(selectedLanguage);
        if (selectedLanguage.value !== languages[0].value) {
          trackAmplitudeData('partnerPortal_action_languageChanged', {
            page_language: selectedLanguage.value,
          });
        }
        saveLocale(selectedLanguage.value);
        await getTranslationList(lang);
      } else {
        await getTranslationList(defaultLocale);
      }
    })();
  }, []);

  const setPath = useCallback(
    (userLoginData: any) => {
      const routesArray: string[] = [];
      let defaultRoute = '';

      if (userLoginData.accounts != null && userLoginData.accounts.length > 0) {
        // All accounts
        const accountCollection = userLoginData.accounts;

        // Loop each account
        accountCollection.forEach((account: any) => {
          const { routeCollection } = account.role;
          defaultRoute = routeCollection.defaultRoute.route;
          const routeCollectionArray = routeCollection.routeCollectionHasRoute;

          routeCollectionArray.forEach((routeIndex: any) => {
            routesArray.push(routeIndex.route);
          });
        });
      }

      let path = defaultRoute;
      if (pagePath) {
        const exist = routesArray.filter((route) => route === pagePath || route.indexOf(pagePath) > -1).length > 0;
        path = exist ? pagePath : defaultRoute;
      }

      const pathname = history.location.pathname.replace(`/${lang}/`, '/');
      history.replace(pathname);
      history.push(path);
    },
    [history, pagePath],
  );

  const getLoginData = useCallback(async () => {
    try {
      const response = await getUserLoginData();
      const userData = response.data;
      if (userData.accounts[0].userRoleName !== 'Partner Representative' && userData.accounts[0].role.id !== 8) {
        removeToken();
        setShowError(true);
      } else {
        setAmplitudeIdentity(userData);
        saveUserData(JSON.stringify(userData));
        setPath(userData);
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
    
      enqueueSnackbar(getTranslation(JSON.parse(translations), errorMessage), {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, saveUserData, setPath, removeToken, translations]);

  useEffect(() => {
    if (authToken) {
      (async () => {
        // const translations = await getTranslations();
        // saveTranslations(JSON.stringify(translations.data));
        await getLoginData();
      })();
    }
  }, [authToken, getLoginData]);

  const loginAsRepresentative = useCallback(
    async (data: LoginForm) => {
      setIsLoading(true);
      setShowError(false);
  
      data.brokerId = process.env.REACT_APP_BROKER_ID;
  
      try {
        const response = await loginRepresentative(data);
        if (response.data.serviceResultStatus !== 1) {
          enqueueSnackbar(
            getTranslation(JSON.parse(translations), response.data.message),
            { variant: 'error' },
          );

          return;
        }
        saveToken(response.data.result);
        await clearThemeCache();
  
        trackAmplitudeData('partnerPortal_login', {
          accountType: 'Representatives',
        });
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error);
  
        enqueueSnackbar(
          getTranslation(JSON.parse(translations), errorMessage),
          { variant: 'error' },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar, setIsLoading, saveToken, translations],
  );

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <>
      {translation && (
        <Grid container direction="column" className={classes.root}>
          <Grid item>
            <UnauthorizedHeader logo={logo} selectedLocale={selectedLocale} handleLocaleChange={handleLocaleChange} />
          </Grid>
          <Grid item className={classes.margin}>
            <Container className={classes.container}>
              <form onSubmit={handleSubmit(loginAsRepresentative)} noValidate>
                <Card className={classes.card}>
                  <CardContent>
                    <Typography className={classes.header}>{translation.text_1360}</Typography>
                    {showError && (
                      <div className={classes.errorMessage}>
                        <CircleIcon height={19} /> {translation.text_1362}
                      </div>
                    )}
                    <div className={classes.padding}>
                      <InputLabel className={classes.label}>
                        {translation.text_1363}
                        <span className={classes.required}>*</span>
                      </InputLabel>
                      <TextField
                        className={classes.input}
                        autoComplete="off"
                        fullWidth
                        id="email"
                        type="email"
                        margin="dense"
                        variant="outlined"
                        placeholder="yourname@gmail.com"
                        error={!!errors.userName}
                        helperText={errors?.userName?.type === 'required' ? translation.text_1358 : errors?.userName?.type === 'validEmail' ? translation.email_must_be_a_valid_email : errors?.userName?.message}
                        inputRef={emailHookRef}
                        {...emailHookRefProps}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <AlternateEmail className={classes.icon} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <InputLabel className={`${classes.padding} ${classes.label}`}>
                        {translation.text_1364}
                        <span className={classes.required}>*</span>
                      </InputLabel>
                      <TextField
                        className={classes.input}
                        autoComplete="off"
                        fullWidth
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        margin="dense"
                        variant="outlined"
                        placeholder={translation.text_1377}
                        error={!!errors.password}
                        helperText={errors?.password?.type === 'required' ? translation.text_1359 : errors?.password?.message}
                        inputRef={passwordHookRef}
                        {...passwordHookRefProps}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
                                {showPassword ? <Visibility className={classes.icon} /> : <VisibilityOff className={classes.icon} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography className={classes.forgotPassword}>
                        {`${translation.forgot_password} `}
                        <Button component={Link} to={PATH_FORGOT_PASSWORD} color="primary">
                          {translation.click_here}
                        </Button>
                      </Typography>
                    </div>
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <Button type="submit" variant="contained" color="primary" fullWidth className={classes.loginBtn} disabled={isLoading}>
                      {translation.text_1365}
                    </Button>
                  </CardActions>
                </Card>
              </form>
            </Container>
          </Grid>
          <Grid item>
            <Footer translation={translation} logo={footerLogo} />
          </Grid>
        </Grid>
      )}
    </>
  );
};
